import { useState, useMemo, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';

export interface CollectionsSharedTransferMultiSelectItem {
  id: string;
  name: string;
  type?: string;
  private?: boolean;
}

export interface CollectionsSharedTransferMultiSelectProps<
  T extends
    CollectionsSharedTransferMultiSelectItem = CollectionsSharedTransferMultiSelectItem,
> {
  options: T[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  listTitle?: string;
  listSubtitle?: string;
  selectedTitle?: string;
  selectedSubtitle?: string;
  searchPlaceholder?: string;
  columnLabelName?: string;
  columnLabelType?: string;
  emptySelectedMessage?: ReactNode;
  itemIcon?: ReactNode;
  height?: string | number;
  getOptionLabel?: (option: T) => string;
  getOptionType?: (option: T) => string;
}

export default function CollectionsSharedTransferMultiSelect<
  T extends CollectionsSharedTransferMultiSelectItem,
>({
  options,
  selectedIds,
  onChange,
  listTitle = 'Items List',
  listSubtitle = 'Select the items to include.',
  selectedTitle = 'Selected Items',
  selectedSubtitle = 'Remove items using the close button.',
  searchPlaceholder = 'Search items...',
  columnLabelName = 'Name',
  columnLabelType = 'Type',
  emptySelectedMessage = 'No items selected yet.',
  itemIcon,
  height = 'clamp(240px, calc(100dvh - 400px), 500px)',
  getOptionLabel = (option) => option.name,
  getOptionType = (option) => (option.private ? 'Private' : 'Public'),
}: CollectionsSharedTransferMultiSelectProps<T>) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const activeInputStyles: SxProps<Theme> = isDarkMode
    ? {
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
          {
            borderColor: theme.palette.semantic.outline.primary,
          },
        '& .MuiInputLabel-root.Mui-focused': {
          color: theme.palette.semantic.text.title3,
        },
      }
    : {};

  const activeCheckboxStyles: SxProps<Theme> = isDarkMode
    ? {
        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
          color: theme.palette.semantic.text.title3,
        },
      }
    : {};

  const [filterSearch, setFilterSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredOptions = useMemo(() => {
    const search = filterSearch.trim().toLowerCase();
    return search
      ? options.filter((option) =>
          getOptionLabel(option).toLowerCase().includes(search)
        )
      : options;
  }, [options, filterSearch, getOptionLabel]);

  const selectedOptions = useMemo(() => {
    const optionMap = new Map(options.map((option) => [option.id, option]));
    return selectedIds
      ? (selectedIds.map((id) => optionMap.get(id)).filter(Boolean) as T[])
      : [];
  }, [options, selectedIds]);

  const visibleOptions = useMemo(() => {
    return filteredOptions.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredOptions, page, rowsPerPage]);

  const visibleOptionIds = useMemo(
    () => visibleOptions.map((opt) => opt.id),
    [visibleOptions]
  );

  const selectedVisibleCount = useMemo(() => {
    return visibleOptionIds.filter((id) => selectedIds.includes(id)).length;
  }, [visibleOptionIds, selectedIds]);

  const allVisibleSelected =
    visibleOptionIds.length > 0 &&
    selectedVisibleCount === visibleOptionIds.length;

  const handleToggleItem = (itemId: string) => {
    if (selectedIds.includes(itemId)) {
      onChange(selectedIds.filter((id) => id !== itemId));
    } else {
      onChange([...selectedIds, itemId]);
    }
  };

  const handleToggleVisibleItems = () => {
    if (allVisibleSelected) {
      onChange(selectedIds.filter((id) => !visibleOptionIds.includes(id)));
    } else {
      onChange([...new Set([...selectedIds, ...visibleOptionIds])]);
    }
  };

  const handleRemoveAll = () => {
    onChange([]);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 2,
      }}
    >
      {/* Left Column: Available Options List */}
      <Stack gap={1}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: theme.palette.semantic.text.title2,
          }}
        >
          {listTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {listSubtitle}
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            height,
            bgcolor: theme.palette.semantic.surface.bg2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}
            >
              <TextField
                id="itemSearch"
                aria-label={searchPlaceholder}
                placeholder={searchPlaceholder}
                size="small"
                autoComplete="off"
                value={filterSearch}
                onChange={(event) => {
                  setFilterSearch(event.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: filterSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Clear filter search"
                        size="small"
                        onClick={() => {
                          setFilterSearch('');
                          setPage(0);
                        }}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ flex: 1, ...activeInputStyles }}
              />
            </Stack>
            <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
              <Table
                size="small"
                stickyHeader
                aria-label={listTitle}
                sx={{
                  '& .MuiTableCell-root': {
                    height: 48,
                    boxSizing: 'border-box',
                    py: 0,
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ height: 48, bgcolor: 'action.hover' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        inputProps={{
                          'aria-label': 'Select all items on this page',
                        }}
                        checked={allVisibleSelected}
                        indeterminate={
                          selectedVisibleCount > 0 && !allVisibleSelected
                        }
                        onChange={handleToggleVisibleItems}
                        sx={activeCheckboxStyles}
                      />
                    </TableCell>
                    <TableCell sx={{ height: 48, fontWeight: 600 }}>
                      {columnLabelName}
                    </TableCell>
                    <TableCell sx={{ height: 48, fontWeight: 600 }}>
                      {columnLabelType}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleOptions.map((option) => {
                    const label = getOptionLabel(option);
                    const isSelected = selectedIds.includes(option.id);
                    return (
                      <TableRow
                        key={option.id}
                        hover
                        onClick={() => handleToggleItem(option.id)}
                        sx={{ height: 48, cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            inputProps={{
                              'aria-label': `Select #${label}`,
                            }}
                            checked={isSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => handleToggleItem(option.id)}
                            sx={activeCheckboxStyles}
                          />
                        </TableCell>
                        <TableCell>{label}</TableCell>
                        <TableCell>{getOptionType(option)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {visibleOptions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No items found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredOptions.length}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Rows per page:"
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                '& .MuiTablePagination-toolbar': {
                  minHeight: 48,
                  pl: 0,
                  pr: 2.5,
                },
              }}
            />
          </Paper>
        </Paper>
      </Stack>

      {/* Right Column: Selected Items */}
      <Stack gap={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle2">
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: theme.palette.semantic.text.title2,
              }}
            >
              {selectedTitle} ({selectedOptions.length})
            </Box>
          </Typography>
          <Button
            aria-label="Remove all selected items"
            startIcon={<DeleteOutlineIcon />}
            size="small"
            disabled={selectedIds.length === 0}
            onClick={handleRemoveAll}
            sx={{
              textTransform: 'none',
              textDecoration: 'underline',
              color: theme.palette.semantic.text.title3,
            }}
          >
            Remove All
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          {selectedSubtitle}
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            height,
            bgcolor: theme.palette.semantic.surface.bg2,
            overflowY: 'auto',
          }}
        >
          {selectedOptions.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 1.5,
              }}
            >
              {selectedOptions.map((option) => {
                const label = getOptionLabel(option);
                return (
                  <Box
                    key={option.id}
                    sx={{
                      minWidth: 0,
                      height: 46,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1,
                      border: 1,
                      borderColor: theme.palette.semantic.outline.primary,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    {itemIcon}
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {label}
                    </Typography>
                    <IconButton
                      aria-label={`Remove #${label}`}
                      size="small"
                      onClick={() => handleToggleItem(option.id)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'grid',
                placeContent: 'center',
                textAlign: 'center',
                px: 2,
              }}
            >
              {typeof emptySelectedMessage === 'string' ? (
                <Typography variant="body2" color="text.secondary">
                  {emptySelectedMessage}
                </Typography>
              ) : (
                emptySelectedMessage
              )}
            </Box>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
